/*
 * PageWright — shared single-page add-to-cart (S4).
 *
 * Real Shopify checkout: POSTs /cart/add.js with the "Collage Art Page" product's tier
 * variant (custom-page-order — real live variants, confirmed against Shopify Admin
 * 2026-07-02) and the EXACT line-item properties the fulfillment webhook expects
 * (~/developer/collagesoup/pagewright packages/api/src/fulfillment.ts `pageLineProps`):
 *   _book_handle → the page's parent book handle
 *   Page         → the page number (string)
 * Getting these property names wrong silently breaks post-purchase delivery — the webhook
 * would find no page-line signature on the order and mint nothing.
 *
 * A third property, _thumb_url (Paul, 2026-07-02: "stuff the thumbnail image of the
 * individual page purchased into the cart... currently just see the default image for
 * custom pages"), carries the REAL page's cover image. "Collage Art Page" is one static
 * product shared by every page purchase, so Shopify's own cart line image would always show
 * the same generic picture — snippets/cart-products.liquid reads this hidden property
 * (underscore-prefixed properties don't show to the customer) and renders it instead of the
 * product's default image when present.
 *
 * VOLUME-TIER FIX (Paul, 2026-07-08 — "after adding 10 images the price in the cart still
 * says 0.99 for number 11+"): every button's data-variant is a STATIC value assigned once at
 * render time (always the tier-1 variant), and this module used to POST it as-is — nothing
 * ever re-priced the cart as it crossed a tier boundary. This is the SAME volume-tier rule
 * packages/domain/src/cart.ts's reducePageCart already solves correctly for the pagewright
 * backend's own (currently unmounted-in-this-theme) cart API — mirrored here by hand since
 * this theme talks straight to Shopify's native /cart/add.js and has no bundler to share that
 * TypeScript module. TIER_VARIANTS below must be kept in sync with
 * packages/contracts/src/pricing.ts PRICING_TIERS in the pagewright repo.
 *
 * Shared by pw-book-pages.js (per-book grid) and pw-image-search.js (cross-catalog search) —
 * same button contract, same checkout path, one implementation. Plain classic script (not a
 * module) so consumers can call it via window.PWPageCart without an import-map/bundler — this
 * theme's other vanilla-JS assets follow the same non-module IIFE pattern. Load this script's
 * <script> tag BEFORE the consumer's, both with `defer`, so it's defined in time.
 */
(function () {
  'use strict';

  // Ascending by minQuantity — mirrors pagewright's packages/contracts/src/pricing.ts.
  var TIER_VARIANTS = [
    { minQuantity: 1, variantId: '52680050835756' }, // 1–10, $0.99/page
    { minQuantity: 11, variantId: '52680050966828' }, // 11–25, $0.69/page
    { minQuantity: 26, variantId: '52680675655980' }, // 26+, $0.59/page
  ];
  var TIER_VARIANT_IDS = TIER_VARIANTS.map(function (t) {
    return t.variantId;
  });

  /** The highest tier whose minQuantity <= quantity (falls back to the lowest tier below it). */
  function resolveTier(quantity) {
    var chosen = TIER_VARIANTS[0];
    for (var i = 0; i < TIER_VARIANTS.length; i++) {
      if (quantity >= TIER_VARIANTS[i].minQuantity) chosen = TIER_VARIANTS[i];
    }
    return chosen;
  }

  function addPageToCart(btn) {
    if (!btn || btn.disabled) return;
    var fallbackVariantId = btn.getAttribute('data-variant');
    var bookHandle = btn.getAttribute('data-parent-handle') || btn.getAttribute('data-book-handle');
    var bookTitle = btn.getAttribute('data-book-title');
    var pageNumber = btn.getAttribute('data-page-number');
    var coverImage = btn.getAttribute('data-cover-image');
    if (!fallbackVariantId || !bookHandle || !pageNumber) return;

    var originalText = btn.textContent;
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.textContent = 'Adding…';

    var newProperties = { _book_handle: bookHandle, Page: pageNumber };
    // Title is the real book name, shown in the native cart in place of the generic shared
    // "Collage Art Page" product title (Paul, 2026-07-08). Not yet threaded through the
    // cross-catalog image-search entry point — that origin still falls back to the generic
    // title until the search API response carries a book title too (follow-up, not this fix).
    if (bookTitle) newProperties.Title = bookTitle;
    if (coverImage) newProperties._thumb_url = coverImage;

    // Read the live cart FIRST — the correct tier depends on the total page count already
    // there, not just this one add. A fetch failure here is non-fatal: fall back to the
    // button's static tier-1 variant rather than blocking the add entirely.
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : { items: [] };
      })
      .catch(function () {
        return { items: [] };
      })
      .then(function (cart) {
        // Real incident, 2026-07-17 (order 87246CS): a customer was charged TWICE for the
        // identical page because nothing checked whether it was already in the cart — only
        // the TOTAL page count was tracked (for tier pricing), not WHICH pages. Block it here,
        // before ever reaching /cart/add.js, rather than relying on fulfillment to silently
        // dedupe post-purchase (which is exactly what left that order PARTIALLY_FULFILLED —
        // she wasn't missing content, but she was billed twice for one file).
        var isDuplicate = (cart.items || []).some(function (item) {
          return item.properties &&
            item.properties._book_handle === bookHandle &&
            item.properties.Page === pageNumber;
        });
        if (isDuplicate) {
          return Promise.resolve({ duplicate: true });
        }

        var existingPageLines = (cart.items || []).filter(function (item) {
          return TIER_VARIANT_IDS.indexOf(String(item.variant_id)) !== -1;
        });
        var existingCount = existingPageLines.reduce(function (sum, item) {
          return sum + item.quantity;
        }, 0);
        var target = resolveTier(existingCount + 1);
        var allAtTarget = existingPageLines.every(function (item) {
          return String(item.variant_id) === target.variantId;
        });

        if (existingPageLines.length === 0 || allAtTarget) {
          // No tier crossing: just add the new page at the (already-correct) target variant.
          return postAdd([{ id: target.variantId, quantity: 1, properties: newProperties }]);
        }

        // Tier crossing: zero every existing page-tier line, then re-add ALL of them (carrying
        // forward their own book/page/thumb properties) plus the new page, at the new tier —
        // the same "zero + rebuild" op-list reducePageCart emits, applied via the native cart API.
        var zeroUpdates = {};
        existingPageLines.forEach(function (item) {
          zeroUpdates[item.key] = 0;
        });
        var rebuildItems = existingPageLines.map(function (item) {
          var props = { _book_handle: item.properties._book_handle, Page: item.properties.Page };
          if (item.properties.Title) props.Title = item.properties.Title;
          if (item.properties._thumb_url) props._thumb_url = item.properties._thumb_url;
          return { id: target.variantId, quantity: item.quantity, properties: props };
        });
        rebuildItems.push({ id: target.variantId, quantity: 1, properties: newProperties });

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: zeroUpdates }),
        }).then(function () {
          return postAdd(rebuildItems);
        });
      })
      .then(function (result) {
        if (result && result.duplicate) {
          btn.textContent = 'Already in cart';
          setTimeout(function () {
            btn.disabled = false;
            btn.removeAttribute('aria-disabled');
            btn.textContent = originalText;
          }, 2000);
          return;
        }
        btn.textContent = 'Added ✓';
        btn.setAttribute('data-pw-added', '');
        refreshCartBubble();
        setTimeout(function () {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('data-pw-added');
          btn.textContent = originalText;
        }, 2000);
      })
      .catch(function () {
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        btn.textContent = 'Try again';
      });
  }

  function postAdd(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    }).then(function (resp) {
      if (!resp.ok) {
        return resp.json().then(function (e) {
          throw new Error((e && e.description) || 'Add to cart failed');
        });
      }
      return resp.json();
    });
  }

/*
 * Updates every cart-bubble on the page (snippets/cart-bubble.liquid — desktop + mobile
 * instances) directly via DOM + /cart.js, rather than through Shopify's native cart-update
 * path. Deliberate: that native path is View-Transition-based and broke site-wide earlier
 * this session from an unrelated duplicate-id bug (see cart-bubble.liquid's id_suffix param) —
 * a plain independent fetch + DOM write here can't trigger the same class of failure.
 */
  function refreshCartBubble() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var counts = document.querySelectorAll('.cart-bubble__text-count');
        for (var i = 0; i < counts.length; i++) {
          counts[i].textContent = cart.item_count;
          counts[i].classList.toggle('hidden', cart.item_count === 0);
        }
        var bubbles = document.querySelectorAll('.cart-bubble');
        for (var j = 0; j < bubbles.length; j++) {
          bubbles[j].classList.toggle('visually-hidden', cart.item_count === 0);
        }
      })
      .catch(function () {
        /* cart count refresh is cosmetic — the add itself already succeeded */
      });
  }

  /*
   * RE-TIER AFTER A REMOVAL (2026-07-30 re-audit, B4).
   *
   * Volume-tier resolution lived ONLY inside addPageToCart, so it ran on the way up and never on
   * the way down. Add 11 pages -> all lines move to tier 2 ($0.69). Remove 5 -> six lines remain
   * at $0.69 even though the cart now qualifies only for tier 1 ($0.99), and Shopify charges the
   * variant price on the line. Checkout takes $4.14 instead of $5.94.
   *
   * It is trivially repeatable on purpose: add 11, remove 10, buy 1 at the bulk rate. Nothing
   * re-prices server-side — there is no Shopify Function or cart transform on this store — so the
   * cart line IS the price. Removal is a real, intended path (snippets/cart-products.liquid,
   * assets/component-cart-items.js posts quantity 0).
   *
   * `packages/domain/src/cart.ts` has always handled this correctly; the theme hand-port
   * implemented only the add half, and the existing tier test only ever asserted the add path.
   *
   * Deliberately a no-op when every page line already sits at the correct tier, so the common
   * case costs one /cart.js read and nothing else. The customer is never overcharged by this —
   * it only ever corrects a line that is priced BELOW what the cart now qualifies for.
   */
  function retierPageLines() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        var pageLines = (cart.items || []).filter(function (item) {
          return TIER_VARIANT_IDS.indexOf(String(item.variant_id)) !== -1;
        });
        if (pageLines.length === 0) return null;

        var count = pageLines.reduce(function (sum, item) { return sum + item.quantity; }, 0);
        var target = resolveTier(count);
        var allAtTarget = pageLines.every(function (item) {
          return String(item.variant_id) === target.variantId;
        });
        if (allAtTarget) return null; // already correct — the overwhelmingly common case

        var zeroUpdates = {};
        pageLines.forEach(function (item) { zeroUpdates[item.key] = 0; });
        var rebuildItems = pageLines.map(function (item) {
          var props = { _book_handle: item.properties._book_handle, Page: item.properties.Page };
          if (item.properties.Title) props.Title = item.properties.Title;
          if (item.properties._thumb_url) props._thumb_url = item.properties._thumb_url;
          return { id: target.variantId, quantity: item.quantity, properties: props };
        });

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: zeroUpdates }),
        }).then(function () {
          return postAdd(rebuildItems);
        });
      })
      .catch(function () {
        /* never block the cart UI on a re-tier; the next add also reconciles */
        return null;
      });
  }

  /*
   * Watch for cart mutations we did not initiate. Removal happens through the theme's own cart
   * components, which post /cart/change.js. Guarded by `retiering` so our OWN rebuild (which
   * posts /cart/update.js + /cart/add.js) cannot recurse.
   */
  var retiering = false;
  function guardedRetier() {
    if (retiering) return;
    retiering = true;
    Promise.resolve(retierPageLines()).then(function () {
      retiering = false;
    }, function () {
      retiering = false;
    });
  }

  if (typeof window.fetch === 'function') {
    var nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var mutating = /\/cart\/(change|update|clear)(\.js)?/.test(String(url));
      return nativeFetch(input, init).then(function (res) {
        if (mutating && !retiering) setTimeout(guardedRetier, 0);
        return res;
      });
    };
  }
  ['cart:updated', 'cart:refresh'].forEach(function (name) {
    document.addEventListener(name, function () { setTimeout(guardedRetier, 60); });
  });

  window.PWPageCart = {
    addPageToCart: addPageToCart,
    refreshCartBubble: refreshCartBubble,
    retierPageLines: retierPageLines,
  };
})();
